import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Megaphone,
} from "lucide-react";
import {
  fetchLICampaigns,
  createLICampaign,
  deleteLICampaign,
  activateLICampaign,
  pauseLICampaign,
  fetchLIICPs,
  fetchLIAccounts,
} from "../../../../../../redux/actions/linkedinOutreachAction";

const STATUS_COLORS = {
  draft: "default",
  active: "success",
  paused: "warning",
  completed: "info",
};

const ROTATION_MODES = [
  { value: "round_robin", label: "Round Robin" },
  { value: "random", label: "Random" },
  { value: "weighted", label: "Weighted" },
];

const INITIAL_FORM = {
  name: "",
  description: "",
  icp_id: "",
  account_ids: [],
  account_rotation_mode: "round_robin",
};

const CampaignList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { campaigns, icps, accounts, loading } = useSelector(
    (state) => state.linkedinOutreach
  );

  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchLICampaigns({ page: page + 1 }));
  }, [dispatch, page]);

  useEffect(() => {
    dispatch(fetchLIICPs());
    dispatch(fetchLIAccounts());
  }, [dispatch]);

  const items = campaigns?.items || [];
  const total = campaigns?.total || 0;

  const headerCellSx = {
    fontWeight: 600,
    fontSize: "0.8rem",
    bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
  };

  const handleOpenCreate = () => {
    setFormData(INITIAL_FORM);
    setCreateOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setFormData(INITIAL_FORM);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await dispatch(createLICampaign(formData));
      handleCloseCreate();
      dispatch(fetchLICampaigns({ page: page + 1 }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (e, id) => {
    e.stopPropagation();
    await dispatch(activateLICampaign(id));
    dispatch(fetchLICampaigns({ page: page + 1 }));
  };

  const handlePause = async (e, id) => {
    e.stopPropagation();
    await dispatch(pauseLICampaign(id));
    dispatch(fetchLICampaigns({ page: page + 1 }));
  };

  const handleDeleteClick = (e, campaign) => {
    e.stopPropagation();
    setDeleteTarget(campaign);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await dispatch(deleteLICampaign(deleteTarget.id));
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
    dispatch(fetchLICampaigns({ page: page + 1 }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatRate = (value) => {
    // Backend returns rates pre-multiplied (e.g. 11.6 for 11.6%) — don't
    // multiply by 100 again. The old formula produced "1160.0%" displays.
    if (value === null || value === undefined) return "-";
    return `${Number(value).toFixed(1)}%`;
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Campaigns ({total})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={handleOpenCreate}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            bgcolor: theme.palette.primary.main,
          }}
        >
          New Campaign
        </Button>
      </Box>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} align="center">Accounts</TableCell>
                <TableCell sx={headerCellSx} align="center">Leads</TableCell>
                <TableCell sx={headerCellSx} align="center">Steps</TableCell>
                <TableCell sx={headerCellSx} align="right">Conn. Rate</TableCell>
                <TableCell sx={headerCellSx} align="right">Reply Rate</TableCell>
                <TableCell sx={headerCellSx}>Created</TableCell>
                <TableCell sx={headerCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 4, color: theme.palette.text.secondary }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Megaphone
                        size={28}
                        color={theme.palette.text.disabled}
                      />
                      <Typography variant="body2">
                        No campaigns yet. Create your first campaign to get
                        started.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    hover
                    onClick={() =>
                      navigate(
                        `/admin/linkedin-outreach/campaigns/${campaign.id}`
                      )
                    }
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {campaign.name}
                      </Typography>
                      {campaign.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            display: "block",
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {campaign.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.status}
                        size="small"
                        color={STATUS_COLORS[campaign.status] || "default"}
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {campaign.account_count ?? 0}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.lead_count ?? 0}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.step_count ?? 0}
                    </TableCell>
                    <TableCell align="right">
                      {formatRate(campaign.connection_rate)}
                    </TableCell>
                    <TableCell align="right">
                      {formatRate(campaign.reply_rate)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {formatDate(campaign.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {["draft", "paused"].includes(campaign.status) && (
                          <Tooltip title="Activate">
                            <IconButton
                              size="small"
                              onClick={(e) => handleActivate(e, campaign.id)}
                            >
                              <Play size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {campaign.status === "active" && (
                          <Tooltip title="Pause">
                            <IconButton
                              size="small"
                              onClick={(e) => handlePause(e, campaign.id)}
                            >
                              <Pause size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {["draft", "completed"].includes(campaign.status) && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(e, campaign)}
                            >
                              <Trash2
                                size={14}
                                color={theme.palette.error?.main}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        )}
      </Paper>

      {/* Create Campaign Dialog */}
      <Dialog
        open={createOpen}
        onClose={handleCloseCreate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>New Campaign</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="Campaign Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>ICP</InputLabel>
              <Select
                value={formData.icp_id}
                onChange={(e) =>
                  setFormData({ ...formData, icp_id: e.target.value })
                }
                label="ICP"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {(icps || []).map((icp) => (
                  <MenuItem key={icp.id} value={icp.id}>
                    {icp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Accounts</InputLabel>
              <Select
                multiple
                value={formData.account_ids}
                onChange={(e) =>
                  setFormData({ ...formData, account_ids: e.target.value })
                }
                label="Accounts"
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const acc = (accounts || []).find((a) => a.id === id);
                      return acc?.linkedin_email || id;
                    })
                    .join(", ")
                }
              >
                {(accounts || []).map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    <Checkbox
                      checked={formData.account_ids.indexOf(acc.id) > -1}
                      size="small"
                    />
                    <ListItemText
                      primary={acc.linkedin_email}
                      secondary={acc.proxy_geo || acc.status}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Account Rotation</InputLabel>
              <Select
                value={formData.account_rotation_mode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    account_rotation_mode: e.target.value,
                  })
                }
                label="Account Rotation"
              >
                {ROTATION_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCreate}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!formData.name || submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {submitting ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteTarget(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignList;
